// =====================================================================
// Career Connect — redux slice (officer side, Phase 1)
// =====================================================================
// Thunks talk to the backend "/career/*" endpoints through the shared
// apiRequest wrapper. State per resource; a shared `loading` flag drives
// the data grid, a separate `addLoading` drives the Post Job dialog.
// =====================================================================

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "../../api/request";

const initialState = {
  jobData: { data: [], meta: {} }, // paginated jobs list (data + meta)
  projects: [], // dropdown: projects
  courses: [], // dropdown: courses of the selected project
  batches: [], // dropdown: batches of the selected course
  locations: [], // suggestions under the Location box (places already used)
  locationList: [], // the Locations screen: same places + how many jobs use each
  locationLoading: false,
  loading: false, // list loading
  addLoading: false, // post-a-job loading
  applicants: { data: [], meta: {} }, // applicants for a job + counts
  applicationDetail: null, // one application (application, student, history, messages)
  jobDetail: null, // full detail of one job (+ enriched targets) for the detail page
  dashboard: null, // officer dashboard (counts + funnel)
  dashboardLoading: false,
  formTemplates: [], // form builder templates (+ their fields)
  formLoading: false,
  statusOptions: [], // manageable application statuses (built-in + custom)
  statusLoading: false,
  eligibleStudents: { data: [], meta: {} }, // students who CAN apply to a job
  detailLoading: false,
  error: null,
};

// ----- Jobs -----
export const getJobs = createAsyncThunk("career/getJobs", async (data = {}) => {
  const res = await apiRequest({
    method: "get",
    url: `career/jobs?page=${data.page || 1}&limit=${data.limit || 10}&filter=${data.filter || ""}`,
  });
  return res;
});

export const addJob = createAsyncThunk("career/addJob", async (data) => {
  const res = await apiRequest({ method: "post", url: `career/jobs`, data });
  return res;
});

export const updateJobStatus = createAsyncThunk(
  "career/updateJobStatus",
  async ({ jobId, job_status }) => {
    const res = await apiRequest({
      method: "patch",
      url: `career/jobs/${jobId}/status`,
      data: { job_status },
    });
    return res;
  }
);

// full job detail (used to pre-fill the edit dialog)
export const getJobById = createAsyncThunk("career/getJobById", async (jobId) => {
  const res = await apiRequest({ method: "get", url: `career/jobs/${jobId}` });
  return res;
});

// edit a posted job (+ optionally re-target its batches)
export const updateJob = createAsyncThunk("career/updateJob", async ({ jobId, ...data }) => {
  const res = await apiRequest({ method: "put", url: `career/jobs/${jobId}`, data });
  return res;
});

// ----- Form builder (templates + fields) -----
export const getFormTemplates = createAsyncThunk("career/getFormTemplates", async () => {
  const res = await apiRequest({ method: "get", url: `career/form-templates` });
  return res;
});
export const createFormTemplate = createAsyncThunk("career/createFormTemplate", async (data) => {
  const res = await apiRequest({ method: "post", url: `career/form-templates`, data });
  return res;
});
export const updateFormTemplateStatus = createAsyncThunk(
  "career/updateFormTemplateStatus",
  async ({ templateId, ...body }) => {
    const res = await apiRequest({ method: "patch", url: `career/form-templates/${templateId}/status`, data: body });
    return res;
  }
);
export const addFormField = createAsyncThunk("career/addFormField", async ({ templateId, ...data }) => {
  const res = await apiRequest({ method: "post", url: `career/form-templates/${templateId}/fields`, data });
  return res;
});
export const deleteFormField = createAsyncThunk("career/deleteFormField", async (fieldId) => {
  const res = await apiRequest({ method: "delete", url: `career/form-fields/${fieldId}` });
  return res;
});
// edit an existing field's label / type / options / required
export const updateFormField = createAsyncThunk("career/updateFormField", async ({ fieldId, ...data }) => {
  const res = await apiRequest({ method: "put", url: `career/form-fields/${fieldId}`, data });
  return res;
});
// edit a template's name / description
export const updateFormTemplate = createAsyncThunk("career/updateFormTemplate", async ({ templateId, ...data }) => {
  const res = await apiRequest({ method: "put", url: `career/form-templates/${templateId}`, data });
  return res;
});

// ----- Application statuses (manageable list: built-in + custom) -----
export const getStatusOptions = createAsyncThunk("career/getStatusOptions", async () => {
  const res = await apiRequest({ method: "get", url: `career/application-statuses` });
  return res;
});
export const createStatusOption = createAsyncThunk("career/createStatusOption", async (data) => {
  const res = await apiRequest({ method: "post", url: `career/application-statuses`, data });
  return res;
});
export const updateStatusOption = createAsyncThunk("career/updateStatusOption", async ({ id, ...data }) => {
  const res = await apiRequest({ method: "put", url: `career/application-statuses/${id}`, data });
  return res;
});
export const deleteStatusOption = createAsyncThunk("career/deleteStatusOption", async (id) => {
  const res = await apiRequest({ method: "delete", url: `career/application-statuses/${id}` });
  return res;
});

// ----- Dashboard -----
export const getDashboard = createAsyncThunk("career/getDashboard", async () => {
  const res = await apiRequest({ method: "get", url: `career/dashboard` });
  return res;
});

// ----- Masters (dropdown sources): project -> course -> batch -----
export const getProjects = createAsyncThunk("career/getProjects", async () => {
  const res = await apiRequest({ method: "get", url: `career/masters/projects` });
  return res;
});

export const getCourses = createAsyncThunk("career/getCourses", async (projectId) => {
  const res = await apiRequest({
    method: "get",
    url: `career/masters/courses?project_id=${projectId}`,
  });
  return res;
});

// Every place a job has already been posted in. Feeds the suggestion list under
// the Location box, so the same town is not typed three different ways.
export const getLocations = createAsyncThunk("career/getLocations", async () => {
  const res = await apiRequest({ method: "get", url: `career/masters/locations` });
  return res;
});

// ----- Locations (the manageable list of places) -----
// getLocations above feeds the suggestion box. These four run the screen, where
// a place can be renamed (which rewrites every job using it) or retired.
export const getLocationList = createAsyncThunk("career/getLocationList", async () => {
  const res = await apiRequest({ method: "get", url: `career/locations` });
  return res;
});

export const createLocation = createAsyncThunk("career/createLocation", async (body) => {
  const res = await apiRequest({ method: "post", url: `career/locations`, data: body });
  return res;
});

export const updateLocation = createAsyncThunk("career/updateLocation", async ({ id, ...body }) => {
  const res = await apiRequest({ method: "put", url: `career/locations/${id}`, data: body });
  return res;
});

export const deleteLocation = createAsyncThunk("career/deleteLocation", async (id) => {
  const res = await apiRequest({ method: "delete", url: `career/locations/${id}` });
  return res;
});

export const getBatches = createAsyncThunk("career/getBatches", async (courseAvailableId) => {
  const res = await apiRequest({
    method: "get",
    url: `career/masters/batches?course_available_id=${courseAvailableId}`,
  });
  return res;
});

// ----- Applicants (officer side) -----
export const getJobApplications = createAsyncThunk("career/getJobApplications", async (jobId) => {
  const res = await apiRequest({ method: "get", url: `career/jobs/${jobId}/applications` });
  return res;
});

export const getEligibleStudents = createAsyncThunk("career/getEligibleStudents", async (jobId) => {
  const res = await apiRequest({ method: "get", url: `career/jobs/${jobId}/eligible-students` });
  return res;
});

export const getApplicationDetail = createAsyncThunk("career/getApplicationDetail", async (applicationId) => {
  const res = await apiRequest({ method: "get", url: `career/applications/${applicationId}` });
  return res;
});

export const updateApplicationStatus = createAsyncThunk(
  "career/updateApplicationStatus",
  async ({ applicationId, ...body }) => {
    const res = await apiRequest({ method: "patch", url: `career/applications/${applicationId}/status`, data: body });
    return res;
  }
);

export const addApplicationHistory = createAsyncThunk(
  "career/addApplicationHistory",
  async ({ applicationId, ...body }) => {
    const res = await apiRequest({ method: "post", url: `career/applications/${applicationId}/history`, data: body });
    return res;
  }
);

export const postApplicationMessage = createAsyncThunk(
  "career/postApplicationMessage",
  async ({ applicationId, ...body }) => {
    const res = await apiRequest({ method: "post", url: `career/applications/${applicationId}/messages`, data: body });
    return res;
  }
);

const careerSlice = createSlice({
  name: "career",
  initialState,
  reducers: {
    // clear the cascading dropdowns (used when the dialog opens/resets)
    clearCourses: (state) => {
      state.courses = [];
    },
    clearBatches: (state) => {
      state.batches = [];
    },
  },
  extraReducers: (builder) => {
    // jobs list
    builder.addCase(getJobs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getJobs.fulfilled, (state, action) => {
      state.loading = false;
      state.jobData = action.payload || { data: [], meta: {} };
    });
    builder.addCase(getJobs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // add job
    builder.addCase(addJob.pending, (state) => {
      state.addLoading = true;
    });
    builder.addCase(addJob.fulfilled, (state) => {
      state.addLoading = false;
    });
    builder.addCase(addJob.rejected, (state, action) => {
      state.addLoading = false;
      state.error = action.error.message;
    });

    // projects
    builder.addCase(getProjects.fulfilled, (state, action) => {
      state.projects = action.payload?.data || [];
    });
    // courses
    builder.addCase(getCourses.fulfilled, (state, action) => {
      state.courses = action.payload?.data || [];
    });
    // batches
    builder.addCase(getBatches.fulfilled, (state, action) => {
      state.batches = action.payload?.data || [];
    });
    // location suggestions
    builder.addCase(getLocations.fulfilled, (state, action) => {
      state.locations = action.payload?.data || [];
    });
    // the Locations screen
    builder.addCase(getLocationList.pending, (state) => {
      state.locationLoading = true;
    });
    builder.addCase(getLocationList.fulfilled, (state, action) => {
      state.locationLoading = false;
      state.locationList = action.payload?.data || [];
    });
    builder.addCase(getLocationList.rejected, (state, action) => {
      state.locationLoading = false;
      state.error = action.error.message;
    });

    // applicants list (+ counts in meta)
    builder.addCase(getJobApplications.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getJobApplications.fulfilled, (state, action) => {
      state.loading = false;
      state.applicants = action.payload || { data: [], meta: {} };
    });
    builder.addCase(getJobApplications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // form templates
    builder.addCase(getFormTemplates.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(getFormTemplates.fulfilled, (state, action) => {
      state.formLoading = false;
      state.formTemplates = action.payload?.data || [];
    });
    builder.addCase(getFormTemplates.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message;
    });

    // application statuses (manageable list)
    builder.addCase(getStatusOptions.pending, (state) => {
      state.statusLoading = true;
    });
    builder.addCase(getStatusOptions.fulfilled, (state, action) => {
      state.statusLoading = false;
      state.statusOptions = action.payload?.data || [];
    });
    builder.addCase(getStatusOptions.rejected, (state, action) => {
      state.statusLoading = false;
      state.error = action.error.message;
    });

    // dashboard
    builder.addCase(getDashboard.pending, (state) => {
      state.dashboardLoading = true;
    });
    builder.addCase(getDashboard.fulfilled, (state, action) => {
      state.dashboardLoading = false;
      state.dashboard = action.payload?.data || null;
    });
    builder.addCase(getDashboard.rejected, (state, action) => {
      state.dashboardLoading = false;
      state.error = action.error.message;
    });

    // one job's full detail
    builder.addCase(getJobById.pending, (state) => {
      state.detailLoading = true;
    });
    builder.addCase(getJobById.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.jobDetail = action.payload?.data || null;
    });
    builder.addCase(getJobById.rejected, (state, action) => {
      state.detailLoading = false;
      state.error = action.error.message;
    });

    // eligible students (who can apply)
    builder.addCase(getEligibleStudents.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getEligibleStudents.fulfilled, (state, action) => {
      state.loading = false;
      state.eligibleStudents = action.payload || { data: [], meta: {} };
    });
    builder.addCase(getEligibleStudents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // one application's detail
    builder.addCase(getApplicationDetail.pending, (state) => {
      state.detailLoading = true;
    });
    builder.addCase(getApplicationDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.applicationDetail = action.payload?.data || null;
    });
    builder.addCase(getApplicationDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.error = action.error.message;
    });
  },
});

export const { clearCourses, clearBatches } = careerSlice.actions;
export default careerSlice.reducer;
